import React from "react";
import { Link } from "react-router-dom";
import { formatPrice, toImg } from "@/api/client";
import { MapPin, Ruler, BedDouble } from "lucide-react";
import FavoriteButton from "@/components/FavoriteButton";

const STATUS_BANNER = {
  reservado: { label: "Reservado", bg: "bg-[#103C4A]" },
  vendido: { label: "Vendido", bg: "bg-stone-800" },
};

export default function PropertyCard({ property, testid }) {
  const status = (property.status || "").toLowerCase();
  const isInactive = status === "reservado" || status === "vendido";

  if (isInactive) {
    const banner = STATUS_BANNER[status];
    return (
      <div
        data-testid={testid || `property-card-${property.slug}`}
        className="block card-soft overflow-hidden select-none cursor-default"
        aria-label={`${property.title} — ${banner.label}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
          <img
            src={toImg(property.main_image)}
            alt={property.title}
            loading="lazy"
            className="h-full w-full object-cover grayscale-[35%] opacity-90"
          />
          <div className="absolute inset-0 bg-stone-950/25" />
          <div
            className={`absolute inset-x-0 top-1/2 -translate-y-1/2 ${banner.bg} py-3 text-center shadow-[0_8px_30px_rgba(0,0,0,0.25)]`}
            data-testid={`property-status-banner-${status}`}
          >
            <span className="font-display text-2xl md:text-3xl text-white tracking-[0.18em] uppercase">
              {banner.label}
            </span>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500">
            <MapPin className="h-3.5 w-3.5" /> {property.locality}
          </div>
          <h3 className="mt-2 text-lg text-stone-900 leading-snug">{property.title}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <FavoriteButton slug={property.slug} />
      <Link
        to={`/imoveis/${property.slug}`}
        data-testid={testid || `property-card-${property.slug}`}
        className="group block card-soft overflow-hidden"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
          <img
            src={toImg(property.main_image)}
            alt={property.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
          />
          <div className="absolute left-4 top-4 flex flex-wrap gap-2">
            <span className="tag-chip bg-white/90 text-[#103C4A]">{property.typology}</span>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500">
            <MapPin className="h-3.5 w-3.5" /> {property.locality}
          </div>
          <h3 className="mt-2 text-lg text-stone-900 leading-snug">{property.title}</h3>
          <div className="mt-4 flex items-center justify-between">
            <div className="font-display text-2xl text-[#103C4A]">
              {formatPrice(property.price, property.currency)}
            </div>
            <div className="flex items-center gap-4 text-xs text-stone-500">
              {property.area && (
                <span className="inline-flex items-center gap-1">
                  <Ruler className="h-3.5 w-3.5" /> {property.area}m²
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <BedDouble className="h-3.5 w-3.5" /> {property.typology}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
